import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ProductGrid, ProductFilters } from '@/components/products';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { products, categories as categoriesApi } from '@/lib/api';
import { Grid, List } from 'lucide-react';
import SmartPagination from '@/components/SmartPagination';
import { getPublicSiteUrl } from '@/lib/seo';
import { PageBreadcrumb } from '@/components/seo/PageBreadcrumb';
import {
  buildListingMetaTitle,
  buildListingMetaDescription,
  getCategoryIntroText,
  truncateMetaDescription,
} from '@/lib/category-seo';
import { getLocaleFromPathname, buildPathForLocale } from '@/lib/intl-seo';
import { buildProductPath } from '@/lib/product-url';
import { renderSocialMetaTags } from '@/components/seo/SocialMetaTags';
import { absoluteImageUrl } from '@/lib/seo';
import { languages } from '@/lib/i18n';
import { GeoFaq } from '@/components/seo/GeoFaq';
import { categoryFaqItems, faqPageJsonLd } from '@/lib/geo-faq';
import { facetLabel, formatAiNumber } from '@/lib/ai-facet-labels';
import { productIdFromCardClick, rememberAiQuery, trackAiEvent } from '@/lib/ai-search-track';

const FACET_KEYS = ['min_price', 'max_price', 'tire_size', 'vehicle_type', 'tire_season', 'rim_size', 'featured', 'source'];
const SUPPORTED_LANG = new Set(languages.map((l) => l.code));
const SEARCH_PAGE_SIZE = 32;
/** Lastik / kategori filtre yan paneli — şimdilik kapalı (true yaparak açabilirsiniz). */
const SHOW_PRODUCT_FILTERS_SIDEBAR = false;

const buildProductListParams = ({
  pageNum,
  sortBy,
  sortOrder,
  effectiveCategory,
  searchParams,
  langCode,
}) => {
  const params = {
    page: pageNum,
    limit: SEARCH_PAGE_SIZE,
    sort: sortBy,
    order: sortOrder,
  };

  const search = searchParams.get('search');
  const minPrice = searchParams.get('min_price');
  const maxPrice = searchParams.get('max_price');
  const featured = searchParams.get('featured');
  const tireSize = searchParams.get('tire_size');
  const vehicleType = searchParams.get('vehicle_type');
  const tireSeason = searchParams.get('tire_season');
  const rimSize = searchParams.get('rim_size');
  const urlSource = searchParams.get('source');

  const currentLang = langCode.substring(0, 2).toLowerCase();
  params.lang = currentLang;

  if (effectiveCategory) params.category = String(effectiveCategory).trim();
  if (search) params.search = search;
  if (minPrice) params.min_price = minPrice;
  if (maxPrice) params.max_price = maxPrice;
  if (featured) params.featured = featured === 'true';
  if (tireSize) params.tire_size = tireSize;
  if (vehicleType) params.vehicle_type = vehicleType;
  if (tireSeason) params.tire_season = tireSeason;
  if (rimSize) params.rim_size = rimSize;
  if (urlSource && String(urlSource).trim()) {
    const src = String(urlSource)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '')
      .slice(0, 48);
    if (src) params.source = src;
  }

  return { params, currentLang };
};

const translateProductList = (rawProducts, currentLang) =>
  (rawProducts || []).map((product) => {
    if (currentLang !== 'tr' && product.translations && product.translations[currentLang]) {
      const translation = product.translations[currentLang];
      return {
        ...product,
        name: translation.name || product.name,
        description: translation.description || product.description,
      };
    }
    return product;
  });

const AI_CHIP_ORDER = [
  'category',
  'gender',
  'color',
  'season',
  'brand',
  'size',
  'material',
  'feature',
  'style',
  'usage',
  'intent',
  'minPrice',
  'maxPrice',
  'city',
];

const isAbortError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError' ||
    message.includes('cancel') ||
    message.includes('abort')
  );
};

const aiFilterChips = (filters, t, lang) => {
  if (!filters || typeof filters !== 'object') return [];
  const chips = [];
  const label = (value) => facetLabel(value, lang);
  for (const key of AI_CHIP_ORDER) {
    const value = filters[key];
    if (value == null || value === '') continue;
    if (key === 'minPrice') {
      chips.push(t('search.aiPriceOver', '{{price}} TL üzeri', { price: formatAiNumber(value, lang) }));
    } else if (key === 'maxPrice') {
      chips.push(t('search.aiPriceUnder', '{{price}} TL altı', { price: formatAiNumber(value, lang) }));
    } else if (key === 'size') {
      chips.push(t('search.aiSize', 'Beden {{size}}', { size: value }));
    } else chips.push(label(value));
  }
  if (filters.productType && String(filters.productType) !== String(filters.category || '')) {
    chips.push(label(filters.productType));
  }
  return chips;
};

async function fetchListingPage({ aiMode, params, signal, t, lang }) {
  const config = signal ? { signal } : {};
  if (!aiMode) {
    const res = await products.list(params, config);
    return { payload: res.data || {}, chips: [], noticeCode: '' };
  }
  try {
    const res = await products.aiSearch(
      {
        query: params.search,
        page: params.page,
        limit: params.limit,
        lang: params.lang,
      },
      config,
    );
    const data = res.data || {};
    const found = data.products || [];
    const page = Number(data.page) || Number(params.page) || 1;
    const pages = Number(data.pages) || 1;
    const limit = Number(params.limit) || found.length;
    let noticeCode = '';
    if (data.engine === 'fallback' || data.engine === 'disabled') noticeCode = 'widened';
    else if (data.filters?.city) noticeCode = 'city';
    return {
      payload: {
        products: found,
        total: data.total || 0,
        page,
        pages,
        has_more: found.length >= limit && page < pages,
      },
      chips: aiFilterChips(data.filters, t, lang),
      noticeCode,
    };
  } catch (error) {
    if (isAbortError(error) || signal?.aborted) throw error;
    const res = await products.list(params, config);
    return { payload: res.data || {}, chips: [], noticeCode: 'unavailable' };
  }
}

const ProductsPage = () => {
  const { lang, categorySlug: categorySlugParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const site = getPublicSiteUrl();
  const baseSite = site ? site.replace(/\/$/, '') : '';
  const [searchParams] = useSearchParams();
  const [productList, setProductList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  /** Sonsuz kaydırma: arama veya kategori vitrin sayfası (?page= yok, has_more veya batch boyutu) */
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [aiChips, setAiChips] = useState([]);
  const [aiNoticeCode, setAiNoticeCode] = useState('');
  const fetchGenerationRef = useRef(0);
  const nextSearchPageRef = useRef(2);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreLockRef = useRef(false);

  /** URL’deki :lang veya kök /products, /category/… için pathname’ten (SEO ile uyumlu) */
  const langCode = useMemo(() => {
    if (lang && SUPPORTED_LANG.has(lang)) return lang;
    const fromPath = getLocaleFromPathname(location.pathname);
    if (fromPath && SUPPORTED_LANG.has(fromPath)) return fromPath;
    return (i18n.language || 'tr').substring(0, 2);
  }, [lang, location.pathname, i18n.language]);

  const homePath = buildPathForLocale(langCode, []);
  const productsListPath = buildPathForLocale(langCode, ['products']);
  const categoryPathFor = (slug) =>
    buildPathForLocale(langCode, ['category', encodeURIComponent(String(slug))]);
  const productPathFor = (product) => buildProductPath(langCode, product);

  const categoryFromQuery = searchParams.get('category');
  const effectiveCategory = categorySlugParam || categoryFromQuery || '';

  const needsCategoryRedirect =
    (location.pathname === '/products' || /^\/[a-z]{2}\/products\/?$/i.test(location.pathname)) &&
    Boolean(categoryFromQuery) &&
    !searchParams.get('search');

  const currentPage = Number(searchParams.get('page')) || 1;
  const sortBy = searchParams.get('sort') || 'created_at';
  const sortOrder = searchParams.get('order') || 'desc';

  const hasSearch = Boolean(searchParams.get('search'));

  useEffect(() => {
    if (searchParams.get('ai') === '1') rememberAiQuery(searchParams.get('search'));
  }, [searchParams]);
  const hasFeatured = searchParams.get('featured') === 'true';
  const listSourceFilter = (() => {
    const raw = (searchParams.get('source') || '').trim();
    if (!raw) return '';
    return raw.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 48);
  })();
  const hasSourceFilter = Boolean(listSourceFilter);
  const isCategoryList = Boolean(effectiveCategory && categorySlugParam);
  const hasFacet = FACET_KEYS.some((k) => {
    const v = searchParams.get(k);
    return v != null && v !== '';
  });
  /** Kategori vitrini (arama/filtre yok): yalnızca sayfa numarası, kaydırarak yükleme yok */
  const indexableCategory = isCategoryList && !hasSearch && !hasFacet;
  /** Arama ve filtreli listeler kaydırarak yüklenir. İndekslenen kategori sayfaları sayfalanır. */
  const useInfiniteScroll =
    !indexableCategory && (hasSearch || isCategoryList || hasFeatured || hasSourceFilter);

  const infiniteScrollFetchKey = useMemo(() => {
    if (!useInfiniteScroll) return '';
    const p = new URLSearchParams(searchParams);
    p.delete('page');
    return [
      p.toString(),
      langCode,
      categorySlugParam || '',
      effectiveCategory || '',
      sortBy,
      sortOrder,
      location.pathname,
    ].join('|');
  }, [
    useInfiniteScroll,
    searchParams,
    langCode,
    categorySlugParam,
    effectiveCategory,
    sortBy,
    sortOrder,
    location.pathname,
  ]);

  const listResetKey = useMemo(() => {
    if (needsCategoryRedirect) return 'redirect';
    if (useInfiniteScroll) return `infinite:${infiniteScrollFetchKey}`;
    return `paginated:${location.search}|${categorySlugParam}|${effectiveCategory}|${location.pathname}|${currentPage}|${sortBy}|${sortOrder}|${langCode}`;
  }, [
    needsCategoryRedirect,
    useInfiniteScroll,
    infiniteScrollFetchKey,
    location.search,
    categorySlugParam,
    effectiveCategory,
    location.pathname,
    currentPage,
    sortBy,
    sortOrder,
    langCode,
  ]);
  const buildListPath = useCallback(
    (nextParams) => {
      const qs = nextParams.toString();
      const qstr = qs ? `?${qs}` : '';
      if (effectiveCategory && categorySlugParam) {
        if (langCode === 'en') {
          return `/category/${encodeURIComponent(categorySlugParam)}${qstr}`;
        }
        return `/${langCode}/category/${encodeURIComponent(categorySlugParam)}${qstr}`;
      }
      if (langCode === 'en') {
        return `/products${qstr}`;
      }
      return `/${langCode}/products${qstr}`;
    },
    [langCode, effectiveCategory, categorySlugParam],
  );

  const pageHref = useCallback(
    (page) => {
      const next = new URLSearchParams(searchParams);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return buildListPath(next);
    },
    [searchParams, buildListPath],
  );

  const navigateWithParams = (mutator) => {
    const next = new URLSearchParams(searchParams);
    mutator(next);
    navigate(buildListPath(next));
  };

  /** Liste sıfırlama: sonsuz kaydırmada ?page= kullanılmaz */
  useLayoutEffect(() => {
    if (needsCategoryRedirect) return;
    setProductList([]);
    setTotalProducts(0);
    setTotalPages(1);
    setSearchHasMore(false);
    setLoading(true);
    setLoadingMore(false);
    setAiChips([]);
    setAiNoticeCode('');
    nextSearchPageRef.current = 2;
    loadMoreLockRef.current = false;
  }, [needsCategoryRedirect, listResetKey]);

  useEffect(() => {
    if (needsCategoryRedirect) return;

    const gen = ++fetchGenerationRef.current;
    const ac = new AbortController();

    const load = async () => {
      try {
        const { params, currentLang } = buildProductListParams({
          pageNum: useInfiniteScroll ? 1 : currentPage,
          sortBy,
          sortOrder,
          effectiveCategory,
          searchParams,
          langCode,
        });

        const aiMode = searchParams.get('ai') === '1' && Boolean(params.search);
        const [productsResult, categoriesResult] = await Promise.allSettled([
          fetchListingPage({ aiMode, params, signal: ac.signal, t, lang: langCode }),
          categoriesApi.list({ signal: ac.signal }),
        ]);

        if (gen !== fetchGenerationRef.current) return;
        if (ac.signal.aborted) return;

        if (productsResult.status === 'fulfilled') {
          const listing = productsResult.value;
          const translatedProducts = translateProductList(
            listing.payload?.products || [],
            currentLang,
          );

          setProductList(translatedProducts);
          setTotalProducts(listing.payload?.total || 0);
          setTotalPages(listing.payload?.pages || 1);
          setAiChips(listing.chips || []);
          setAiNoticeCode(listing.noticeCode || '');
          const firstBatch = translatedProducts;
          const pageNum = listing.payload?.page ?? 1;
          const pagesTotal = listing.payload?.pages ?? 1;
          setSearchHasMore(
            hasSearch
              ? listing.payload?.has_more === true
              : hasFeatured || hasSourceFilter
                ? pageNum < pagesTotal || firstBatch.length >= SEARCH_PAGE_SIZE
                : firstBatch.length >= SEARCH_PAGE_SIZE,
          );
          if (useInfiniteScroll) {
            nextSearchPageRef.current = 2;
          }
        }

        if (categoriesResult.status === 'fulfilled') {
          setCategories(categoriesResult.value.data || []);
        }
      } catch (error) {
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
        console.error('Failed to fetch products:', error);
      } finally {
        if (gen === fetchGenerationRef.current && !ac.signal.aborted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => ac.abort();
  }, [needsCategoryRedirect, listResetKey, langCode, categorySlugParam]);

  /** Sonsuz kaydırmada ?page= kaldır */
  useEffect(() => {
    if (!useInfiniteScroll || needsCategoryRedirect || indexableCategory) return;
    const p = searchParams.get('page');
    if (!p || p === '1') return;
    const next = new URLSearchParams(searchParams);
    next.delete('page');
    const qs = next.toString();
    navigate(`${buildListPath(next)}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [useInfiniteScroll, needsCategoryRedirect, indexableCategory, searchParams, navigate, buildListPath]);

  const loadMoreSearch = useCallback(async () => {
    if (!useInfiniteScroll || !searchHasMore || loading || loadingMore || loadMoreLockRef.current) return;
    loadMoreLockRef.current = true;
    setLoadingMore(true);
    try {
      const pageNum = nextSearchPageRef.current;
      const { params, currentLang } = buildProductListParams({
        pageNum,
        sortBy,
        sortOrder,
        effectiveCategory,
        searchParams,
        langCode,
      });
      const aiMode = searchParams.get('ai') === '1' && Boolean(params.search);
      const listing = await fetchListingPage({ aiMode, params, t, lang: langCode });
      const batch = translateProductList(listing.payload?.products || [], currentLang);
      const pageNumFromApi = listing.payload?.page ?? pageNum;
      const pagesTotal = listing.payload?.pages ?? pageNumFromApi;
      setProductList((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const merged = [...prev];
        for (const item of batch) {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            merged.push(item);
          }
        }
        return merged;
      });
      setSearchHasMore(
        hasSearch
          ? listing.payload?.has_more === true
          : hasFeatured || hasSourceFilter
            ? pageNumFromApi < pagesTotal || batch.length >= SEARCH_PAGE_SIZE
            : batch.length >= SEARCH_PAGE_SIZE,
      );
      setTotalProducts(listing.payload?.total ?? 0);
      nextSearchPageRef.current = pageNum + 1;
    } catch (e) {
      console.error('Load more search failed:', e);
    } finally {
      loadMoreLockRef.current = false;
      setLoadingMore(false);
    }
  }, [
    useInfiniteScroll,
    searchHasMore,
    loading,
    loadingMore,
    sortBy,
    sortOrder,
    effectiveCategory,
    searchParams,
    langCode,
    hasSearch,
    hasFeatured,
    hasSourceFilter,
  ]);

  useEffect(() => {
    if (!useInfiniteScroll || !searchHasMore || loading) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreSearch();
      },
      { root: null, rootMargin: '240px', threshold: 0 },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [useInfiniteScroll, searchHasMore, loading, loadMoreSearch, productList.length]);

  const handleSortChange = (value) => {
    navigateWithParams((next) => {
      const [sort, order] = value.split('-');
      next.set('sort', sort);
      next.set('order', order);
      if (useInfiniteScroll) {
        next.delete('page');
      } else {
        next.set('page', '1');
      }
    });
  };

  const handlePageChange = (page) => {
    navigateWithParams((next) => {
      next.set('page', String(page));
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageTitleBase = () => {
    const search = searchParams.get('search');
    const featured = searchParams.get('featured');

    if (search && searchParams.get('ai') === '1') {
      return `${t('search.aiResults', 'AI arama')}: "${search}"`;
    }
    if (search) return `${t('common.search', 'Arama')}: "${search}"`;
    if (featured) return t('home.featuredProducts');
    if (effectiveCategory) {
      const cat = categories.find((c) => c.slug === effectiveCategory);
      return cat?.name || t('common.products');
    }
    return t('nav.allProducts', 'Tüm Ürünler');
  };

  const getPageTitle = () => {
    const base = getPageTitleBase();
    if (!useInfiniteScroll && currentPage > 1) {
      return `${base} | ${t('seo.pageN', 'Sayfa')} ${currentPage}`;
    }
    return base;
  };

  const h1Title = getPageTitle();

  const shouldNoIndex = hasSearch || hasFacet;

  let listMode = 'all';
  if (hasSearch) listMode = 'search';
  else if (hasFeatured) listMode = 'featured';
  else if (effectiveCategory && categorySlugParam) listMode = 'category';

  const currentCategoryDoc =
    listMode === 'category' ? categories.find((c) => c.slug === effectiveCategory) : null;
  const categoryDisplayName = currentCategoryDoc?.name || '';

  const metaTitle = searchParams.get('ai') === '1' && hasSearch
    ? `${t('search.aiResults', 'AI arama')}: "${searchParams.get('search')}" | Turkish E-Market`
    : buildListingMetaTitle({
        t,
        mode: listMode,
        categoryName: categoryDisplayName,
        page: useInfiniteScroll ? 1 : currentPage,
        searchQuery: searchParams.get('search') || '',
      });

  const metaDescriptionRaw = buildListingMetaDescription({
    t,
    mode: listMode,
    categoryName: categoryDisplayName,
    totalProducts: useInfiniteScroll ? productList.length : totalProducts,
    page: useInfiniteScroll ? 1 : currentPage,
  });

  const metaDescription = truncateMetaDescription(
    hasSearch
      ? t(
          'seo.internalSearchDescription',
          'Site içi arama sonuçları; bu sayfa arama motorlarında indekslenmez.',
        )
      : metaDescriptionRaw,
  );

  const canonicalHref = () => {
    if (!site) return null;
    if (shouldNoIndex) {
      if (effectiveCategory && categorySlugParam) {
        return `${baseSite}${categoryPathFor(categorySlugParam)}`;
      }
      return `${baseSite}${productsListPath}`;
    }
    return `${site}${location.pathname}${location.search}`;
  };

  const breadcrumbNavItems = (() => {
    const home = { label: t('nav.home'), to: homePath };
    const productsLink = { label: t('nav.products'), to: productsListPath };
    if (listMode === 'category' && categoryDisplayName && categorySlugParam) {
      return [home, productsLink, { label: categoryDisplayName, to: null }];
    }
    if (listMode === 'search') {
      return [home, productsLink, { label: t('common.search', 'Arama'), to: null }];
    }
    if (listMode === 'featured') {
      return [home, productsLink, { label: t('home.featuredProducts'), to: null }];
    }
    if (listMode === 'all' && !hasSearch && !hasFeatured) {
      return [home, { label: t('nav.allProducts', 'Tüm Ürünler'), to: null }];
    }
    return [home, productsLink, { label: h1Title, to: null }];
  })();

  const showIntro =
    !hasSearch &&
    !hasFacet &&
    (listMode === 'category' || (listMode === 'all' && !hasFeatured));

  const introText =
    listMode === 'category' && currentCategoryDoc
      ? getCategoryIntroText(currentCategoryDoc, t)
      : listMode === 'all' && !hasFeatured
        ? t(
            'seo.allProductsIntro',
            'Turkish E-Market’te gıdadan giyime, ev yaşamından elektronik ürünlere kadar geniş bir yelpazede alışveriş yapın. Sıralama seçenekleriyle ihtiyacınıza uygun ürünü kolayca bulun.',
          )
        : '';

  const categoryFaq =
    showIntro && listMode === 'category'
      ? categoryFaqItems(categoryDisplayName, totalProducts, t)
      : [];
  const categoryFaqLd = faqPageJsonLd(categoryFaq);

  const breadcrumbJsonLd = (() => {
    if (!site) return '';
    const els = [];
    els.push({
      name: t('nav.home'),
      item: `${baseSite}${homePath === '/' ? '' : homePath}`,
    });
    const here = `${site}${location.pathname}${location.search}`;
    if (listMode === 'category' && categoryDisplayName && categorySlugParam) {
      els.push({ name: t('nav.products'), item: `${baseSite}${productsListPath}` });
      els.push({
        name: categoryDisplayName,
        item: `${baseSite}${categoryPathFor(categorySlugParam)}`,
      });
    } else if (listMode === 'search') {
      els.push({ name: t('nav.products'), item: `${baseSite}${productsListPath}` });
      els.push({ name: t('common.search', 'Arama'), item: here });
    } else if (listMode === 'featured') {
      els.push({ name: t('nav.products'), item: `${baseSite}${productsListPath}` });
      els.push({ name: t('home.featuredProducts'), item: here });
    } else if (listMode === 'all' && !hasSearch && !hasFeatured) {
      els.push({ name: t('nav.allProducts', 'Tüm Ürünler'), item: here });
    } else {
      els.push({ name: t('nav.products'), item: `${baseSite}${productsListPath}` });
      els.push({ name: h1Title, item: here });
    }
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: els.map((e, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: e.name,
        item: e.item,
      })),
    });
  })();

  const webPageJsonLd =
    site && canonicalHref() && !shouldNoIndex
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: metaTitle.replace(/\s*\|\s*Turkish E-Market\s*$/, '').trim(),
          description: metaDescription,
          url: canonicalHref(),
          isPartOf: {
            '@type': 'WebSite',
            name: 'Turkish E-Market',
            url: site,
          },
        })
      : '';

  const itemListJsonLd =
    site &&
    productList.length > 0 &&
    !shouldNoIndex &&
    listMode !== 'search'
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: (useInfiniteScroll ? productList.slice(0, 24) : productList).map((p, i) => ({
            '@type': 'ListItem',
            position: (useInfiniteScroll ? 0 : (currentPage - 1) * SEARCH_PAGE_SIZE) + i + 1,
            name: p.name,
            url: `${baseSite}${productPathFor(p)}`,
          })),
        })
      : '';

  const listingCanonical = canonicalHref();
  const listingOgImage = (() => {
    const catImg = currentCategoryDoc?.image;
    if (catImg && site) return absoluteImageUrl(site, catImg);
    const first = productList?.[0]?.images?.[0];
    if (first && site) return absoluteImageUrl(site, first);
    return site ? `${site}/logo.png` : '';
  })();

  if (needsCategoryRedirect) {
    const q = categoryFromQuery;
    const next = new URLSearchParams(searchParams);
    next.delete('category');
    const qs = next.toString();
    return (
      <Navigate
        to={`${categoryPathFor(q)}${qs ? `?${qs}` : ''}`}
        replace
      />
    );
  }

  return (
    <div
      className="py-4 sm:py-6"
      data-testid="products-page"
      data-seo-ready={!loading ? 'true' : undefined}
    >
      <Helmet htmlAttributes={{ lang: langCode }}>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {shouldNoIndex ? <meta name="robots" content="noindex,follow" /> : null}
        {listingCanonical ? <link rel="canonical" href={listingCanonical} /> : null}
        {indexableCategory && currentPage > 1 ? (
          <link rel="prev" href={`${baseSite}${pageHref(currentPage - 1)}`} />
        ) : null}
        {indexableCategory && currentPage < totalPages ? (
          <link rel="next" href={`${baseSite}${pageHref(currentPage + 1)}`} />
        ) : null}
        {!shouldNoIndex
          ? renderSocialMetaTags({
              title: metaTitle,
              description: metaDescription,
              url: listingCanonical,
              image: listingOgImage,
              type: 'website',
              locale: langCode,
            })
          : null}
        {breadcrumbJsonLd ? (
          <script type="application/ld+json">{breadcrumbJsonLd}</script>
        ) : null}
        {webPageJsonLd ? <script type="application/ld+json">{webPageJsonLd}</script> : null}
        {itemListJsonLd ? <script type="application/ld+json">{itemListJsonLd}</script> : null}
        {categoryFaqLd ? <script type="application/ld+json">{categoryFaqLd}</script> : null}
      </Helmet>
      <div className="max-w-[1920px] mx-auto px-2 sm:px-4">
        <PageBreadcrumb items={breadcrumbNavItems} />

        <div className="max-w-[1400px] mx-auto w-full">
          <div
            className={
              SHOW_PRODUCT_FILTERS_SIDEBAR
                ? 'flex flex-col lg:flex-row gap-6 lg:gap-8 items-start'
                : 'flex flex-col gap-6 items-start'
            }
          >
            {SHOW_PRODUCT_FILTERS_SIDEBAR ? (
              <aside
                className="w-full lg:w-72 xl:w-80 shrink-0 lg:sticky lg:top-20 z-10"
                aria-label={t('filters.title')}
              >
                <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b bg-muted/30">
                    <h2 className="text-sm font-semibold tracking-tight">{t('filters.title')}</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('filters.sidebarHint')}</p>
                  </div>
                  <div className="p-3 sm:p-4 max-h-[min(70vh,900px)] overflow-y-auto">
                    <ProductFilters categories={categories} />
                  </div>
                </div>
              </aside>
            ) : null}

            <div className={SHOW_PRODUCT_FILTERS_SIDEBAR ? 'flex-1 min-w-0 w-full' : 'w-full'}>
          <div className="mb-4 sm:mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-1">{h1Title}</h1>
              <p className="text-muted-foreground text-sm">
                {useInfiniteScroll
                  ? `${productList.length}${searchHasMore ? '+' : ''} ${t('common.productsFound', 'ürün bulundu')}`
                  : `${totalProducts} ${t('common.productsFound', 'ürün bulundu')}`}
              </p>
              {showIntro && introText ? (
                <p
                  className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-500"
                  aria-label={t('seo.introSectionLabel', 'Kategori açıklaması')}
                >
                  {introText}
                </p>
              ) : null}
              {aiChips.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="ai-filter-chips">
                  {aiChips.map((chip) => (
                    <span
                      key={chip}
                      className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-800"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              ) : null}
              {aiNoticeCode === 'widened' ? (
                <p className="text-xs text-muted-foreground mt-2" data-testid="ai-search-notice">
                  {t('search.aiWidened', 'Dar sonuç çıkmadı. Fiyat korunarak daha geniş arama kullanıldı.')}
                </p>
              ) : null}
              {aiNoticeCode === 'unavailable' ? (
                <p className="text-xs text-muted-foreground mt-2" data-testid="ai-search-notice">
                  {t('search.aiUnavailable', 'AI araması açılamadı. Aynı cümle normal ürün aramasında gösteriliyor.')}
                </p>
              ) : null}
              {aiNoticeCode === 'city' ? (
                <p className="text-xs text-muted-foreground mt-2" data-testid="ai-search-notice">
                  {t('search.aiCityNote', 'Şehir bilgisi listeyi daraltmaz.')}
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 sm:gap-4 pb-3 sm:pb-0 border-b sm:border-0">
              <Select value={`${sortBy}-${sortOrder}`} onValueChange={handleSortChange}>
                <SelectTrigger className="w-full sm:w-48" data-testid="sort-select">
                  <SelectValue placeholder={t('filters.sortBy')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at-desc">Newest First</SelectItem>
                  <SelectItem value="created_at-asc">Oldest First</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                  <SelectItem value="rating-desc">Highest Rated</SelectItem>
                  <SelectItem value="name-asc">Name: A-Z</SelectItem>
                </SelectContent>
              </Select>

              <div className="hidden md:flex items-center border rounded-lg shrink-0">
                <Button variant="ghost" size="icon" className="rounded-r-none" data-testid="grid-view-btn">
                  <Grid className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="rounded-l-none" data-testid="list-view-btn">
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {showIntro && listMode === 'category' ? <GeoFaq items={categoryFaq} className="mb-6" /> : null}

          <div
            onClickCapture={(event) => {
              if (searchParams.get('ai') !== '1') return;
              const productId = productIdFromCardClick(event);
              if (productId) trackAiEvent('click', productId);
            }}
          >
          <ProductGrid
            key={useInfiniteScroll ? `scroll-${infiniteScrollFetchKey}` : `${location.pathname}${location.search}`}
            listContextKey={useInfiniteScroll ? `scroll-${infiniteScrollFetchKey}` : `${location.pathname}${location.search}`}
            products={productList}
            loading={loading}
            columns={8}
          />
          </div>

          {useInfiniteScroll ? (
            <>
              <div ref={loadMoreSentinelRef} className="h-4 w-full" aria-hidden data-testid="infinite-load-sentinel" />
              {loadingMore ? (
                <p className="text-center text-sm text-muted-foreground py-6">{t('common.loading', 'Yükleniyor…')}</p>
              ) : null}
              {!loading && !searchHasMore && productList.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('search.noResults', 'Sonuç bulunamadı')}</p>
              ) : null}
            </>
          ) : (
            <SmartPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalProducts}
              pageSize={SEARCH_PAGE_SIZE}
              onPageChange={handlePageChange}
              hasMore={false}
              hrefForPage={!shouldNoIndex ? pageHref : undefined}
            />
          )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
